import React, { useState, useEffect, useContext } from 'react';
import { ThemeContext } from 'styled-components';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useTable, useColumnOrder } from 'react-table';
// styles
import {
  CheckmarkBoldIconStyled,
  DivStyledTableContainer,
  TableDataStyledBody,
  TableHeadingStyledBody,
  TableHeadingStyledCell,
  TableRowStyled,
  TableRowStyledHeading,
  TableStyled,
  TypographyStyledHeading,
} from './styles';

export const ProductComparisonChartModule = ({
  className,
  id,
  'data-static-config': staticConfig,
}) => {
  const classNames = {};
  classNames[className] = className;
  const moduleConfig = staticConfig.productComparisonChart;

  const theme = useContext(ThemeContext);
  const mediumDown = theme.mediumDown();

  const [isMobile, setIsMobile] = useState(!mediumDown);

  const {
    headerGroups,
    getTableProps,
    getTableBodyProps,
    setHiddenColumns,
    prepareRow,
    rows,
  } = useTable(
    {
      columns: moduleConfig.columns,
      data: moduleConfig.data,
      defaultCanFilter: true,
    },
    useColumnOrder,
  );

  function handleResize(checkIsMobile) {
    if (checkIsMobile) {
      // manually reset visibility for all columns on desktop viewport
      setHiddenColumns([]);
    }
  }

  useEffect(() => {
    if (!mediumDown !== isMobile) {
      setIsMobile(!mediumDown);
      handleResize(!mediumDown);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediumDown]);

  return (
    <>
      <DivStyledTableContainer
        id={`${id}-product-comparison-table-container`}
        className={classnames(classNames, className)}>
        {moduleConfig.sectionHeading && (
          <TypographyStyledHeading
            component='h2'
            disableBottomPadding
            className={classnames({ 'nmx-assistive-text': moduleConfig.hideSectionHeading })}>
            {moduleConfig.sectionHeading}
          </TypographyStyledHeading>
        )}
        <TableStyled {...getTableProps()}>
          <caption>
            Compare life insurance products
          </caption>
          <thead>
            {// Loop over the header rows
              headerGroups.map((headerGroup) => (
                // Apply the header row props
                <TableRowStyledHeading {...headerGroup.getHeaderGroupProps()}>
                  {// Loop over the headers in each row
                    headerGroup.headers.map((column) => (
                      <TableHeadingStyledCell
                        key={`product-comparison-chart-header-${column.Header}`}
                        scope='col'
                        {...column.getHeaderProps()}
                        className={column.headerCellClassName}>
                        <>
                          {column.render('Header')}
                        </>
                      </TableHeadingStyledCell>
                    ))
                  }
                </TableRowStyledHeading>
              ))
            }
          </thead>
          {/* Apply the table body props */}
          <tbody {...getTableBodyProps()}>
            {// Loop over the table rows
              rows.map((row) => {
                // Prepare the row for display
                prepareRow(row);
                return (
                // Apply the row props
                  <TableRowStyled {...row.getRowProps()}>
                    {// Loop over the rows cells
                      row.cells.map((cell, index) => {
                        if (index > 0) {
                          return (
                            <TableDataStyledBody {...cell.getCellProps()}>
                              {cell.value === 'true' ? <CheckmarkBoldIconStyled /> : cell.value }
                            </TableDataStyledBody>
                          );
                        }
                        return (
                          <TableHeadingStyledBody
                            scope='row'
                            {...cell.getCellProps()}>
                            {cell.render('Cell')}
                          </TableHeadingStyledBody>
                        );
                      })
                    }
                  </TableRowStyled>
                );
              })
            }
          </tbody>
        </TableStyled>
      </DivStyledTableContainer>
    </>
  );
};

ProductComparisonChartModule.propTypes = {
  'data-static-config': PropTypes.object,
  /** Custom class name to add to Type. */
  className: PropTypes.string,
  /** Hide section heading from the screen: if true will be converted to assistive-text.   */
  hideSectionHeading: PropTypes.bool,
  /** Id for the section */
  id: PropTypes.string.isRequired,
};

ProductComparisonChartModule.defaultProps = {
  className: null,
  hideSectionHeading: false,
};

export default ProductComparisonChartModule;
